// The top line of every page (um/2026-10-30/04, DESIGN.md «Верхняя строка»): one line of one height, three zones in one order — what is
// open (view, crumbs, title) | filters and the search | the count, the actions and «+». Screens give only the contents of the zones;
// on a phone the line stays one line: the actions but the main one fold into «Ещё», the search opens over the line.

import { createContext, useContext, useState, type ReactNode, type RefObject } from 'react';
import { Icon } from './icons.tsx';
import { Button, IconButton, Menu, MenuContent, MenuItem, MenuSeparator, MenuTrigger, SearchField, cx, useNarrow } from './primitives.tsx';
import { useTooltip } from './Tooltip.tsx';
import s from './top-bar.module.css';

/** An action of the line: a button on a wide screen; on a phone one of «Ещё», unless it is the main one. */
export interface TopAction {
  key: string; label: string; icon?: string; onClick: () => void;
  /** The main action (Опубликовать, + Запись): always a button, the last one at the right edge. */
  primary?: boolean; disabled?: boolean; danger?: boolean; title?: string;
  /** A tool known by its icon (the gear of the fields): the label is its hint and its line in «Ещё». */
  iconOnly?: boolean; pressed?: boolean;
  /** A choice of what to do (a new form of a kind): the button opens these items. */
  menu?: { key: string; label: string; icon?: string; onSelect: () => void }[];
}

export interface TopSearch {
  value: string; onChange: (q: string) => void; placeholder: string;
  /** The name of the field for a screen reader and the scenarios; «Поиск» by default. */
  label?: string;
  inputRef?: RefObject<HTMLInputElement | null>;
  /** «/» focuses this search (one per page: the screen's main search). */
  slash?: boolean;
}

/**
 * Where the actions of the line go: the main ones are buttons at the right edge on every width; the rest are buttons on a wide screen
 * and lines of «Ещё» on a phone, so the line stays one line.
 */
export function foldActions(actions: TopAction[], narrow: boolean): { main: TopAction[]; rest: TopAction[]; folded: TopAction[] } {
  const main = actions.filter((a) => a.primary);
  const others = actions.filter((a) => !a.primary);
  return narrow ? { main, rest: [], folded: others } : { main, rest: others, folded: [] };
}

/** The way back to the list of «list | reading» on a phone: the line of the reading column shows it first. */
export const TopBack = createContext<(() => void) | null>(null);
/**
 * The way to the left column on a phone, where it is a drawer (BUG-10 of the review of «Настройки»): the frame gives it, the line shows
 * it first, so the items of a section are one tap away on every page, not behind the small button at the bottom of the rail.
 */
export const TopNav = createContext<(() => void) | null>(null);

export function TopBar({ start, heading, middle, search, count, end, actions = [], more, label, className }: {
  start?: ReactNode;
  /**
   * The page's H1 when what is open is not a title in the line (the menu of a view, the days of the schedule, a name edited in place):
   * one H1 on every page (DESIGN.md «Верхняя строка»), read by a screen reader, not shown twice.
   */
  heading?: string;
  middle?: ReactNode; search?: TopSearch; count?: ReactNode; end?: ReactNode;
  actions?: TopAction[];
  /** Items of «Ещё» on every width (MenuItem). */
  more?: ReactNode;
  label?: string; className?: string;
}) {
  const narrow = useNarrow();
  const back = useContext(TopBack);
  const openNav = useContext(TopNav);
  const [searching, setSearching] = useState(false);
  const { main, rest, folded } = foldActions(actions, narrow);
  const searchOpen = !!search && (!narrow || searching || !!search.value);
  return (
    <header className={cx(s.bar, narrow && searching && s.searching, className)} aria-label={label}>
      {heading && <h1 className={s.srOnly}>{heading}</h1>}
      {back && <IconButton size="sm" icon="back" label="К списку" onClick={back} className={s.back} />}
      {!back && narrow && openNav && <IconButton size="sm" icon="sidebar" label="Меню раздела" onClick={openNav} className={s.back} />}
      {start && <div className={s.start}>{start}</div>}
      {middle && <div className={s.middle}>{middle}</div>}
      {search && (searchOpen
        ? <SearchField ghost size="sm" ref={search.inputRef} value={search.value} onChange={search.onChange} placeholder={search.placeholder} label={search.label ?? 'Поиск'}
          slash={search.slash} autoFocus={narrow && searching} onClose={narrow ? () => { search.onChange(''); setSearching(false); } : undefined} className={s.search} />
        : <IconButton size="sm" icon="search" label="Поиск" onClick={() => setSearching(true)} />)}
      <span className={s.spacer} />
      {count != null && <span className={s.count} aria-live="polite">{count}</span>}
      {end}
      {rest.map((a) => <ActionButton key={a.key} a={a} />)}
      {(folded.length > 0 || more) && (
        <Menu>
          <MenuTrigger asChild><IconButton size="sm" icon="more" label="Ещё" /></MenuTrigger>
          <MenuContent align="end">
            {folded.flatMap((a) => (a.menu ? a.menu.map((m) => <MenuItem key={`${a.key}.${m.key}`} icon={m.icon ?? a.icon} onSelect={m.onSelect}>{`${a.label}: ${m.label}`}</MenuItem>)
              : [<MenuItem key={a.key} icon={a.icon} danger={a.danger} disabled={a.disabled} onSelect={a.onClick}>{a.label}</MenuItem>]))}
            {folded.length > 0 && more && <MenuSeparator />}
            {more}
          </MenuContent>
        </Menu>
      )}
      {main.map((a) => <ActionButton key={a.key} a={a} />)}
    </header>
  );
}

function ActionButton({ a }: { a: TopAction }) {
  if (a.menu) {
    return (
      <Menu>
        <MenuTrigger asChild>
          <Button size="sm" variant={a.primary ? 'primary' : 'secondary'} icon={a.icon} disabled={a.disabled} aria-label={a.label} caret><span className={a.icon ? s.label : undefined}>{a.label}</span></Button>
        </MenuTrigger>
        <MenuContent align="end">{a.menu.map((m) => <MenuItem key={m.key} icon={m.icon} onSelect={m.onSelect}>{m.label}</MenuItem>)}</MenuContent>
      </Menu>
    );
  }
  if (a.iconOnly) return <IconButton size="sm" icon={a.icon ?? 'more'} pressed={a.pressed} disabled={a.disabled} label={a.label} hint={a.title} onClick={a.onClick} />;
  return <LabelledAction a={a} />;
}

/** A button with its text; the text hides on a phone and in a narrow column, so the name is also the tooltip (with the key, if any). */
function LabelledAction({ a }: { a: TopAction }) {
  const narrow = useNarrow();
  const tip = !!a.icon && (narrow || !!a.title);
  const { handlers, node } = useTooltip<HTMLButtonElement>(a.title ? `${a.label} (${a.title})` : a.label, 'bottom');
  return (
    <Button size="sm" variant={a.primary ? 'primary' : 'secondary'} icon={a.icon} disabled={a.disabled} aria-label={a.label} onClick={a.onClick} {...(tip ? handlers : {})}>
      <span className={a.icon ? s.label : undefined}>{a.label}</span>{tip && node}
    </Button>
  );
}

/**
 * What is open: an icon, the name and a note after it, in one line; the name is cut, never wrapped. It is the page's H1 (DESIGN.md
 * «Верхняя строка»); `as` lowers it where the page has its H1 elsewhere (a card in the reading column: its title is the heading).
 */
export function TopTitle({ icon, children, note, as: Tag = 'h1' }: { icon?: string; children: ReactNode; note?: ReactNode; as?: 'h1' | 'h2' | 'span' }) {
  return (
    <Tag className={s.title}>
      {icon && <Icon name={icon} size={15} />}
      <span className={s.titleText}>{children}</span>
      {note && <span className={s.note}>{note}</span>}
    </Tag>
  );
}

/** The name of what is open, edited in place (a form, a page of the site, a scenario): the look of TopTitle. */
export function TopTitleInput({ value, onChange, onCommit, label, disabled, placeholder }: {
  value: string; onChange: (v: string) => void; onCommit?: () => void; label: string; disabled?: boolean; placeholder?: string;
}) {
  return (
    <input className={s.titleInput} value={value} disabled={disabled} placeholder={placeholder} aria-label={label} size={Math.max(8, Math.min(48, value.length + 1))}
      onChange={(e) => onChange(e.target.value)} onBlur={onCommit} onKeyDown={(e) => { if (e.key === 'Enter') (e.target as HTMLInputElement).blur(); }} />
  );
}

export const topBarCss = s;
